import React from "react";
import styled, { css } from "styled-components";
import { Button as UppButton } from "@creditsafe/ui-components";

export type variants =
  | "primary"
  | "primary-outline"
  | "grey"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "light"
  | "dark"
  | "link"
  | "transparent"
  | "no-variant";

interface BaseButtonProps {
  variant: variants;
  disabled?: boolean;
  children: React.ReactNode;
  type?: "button" | "submit" | "reset";
  onClick?: () => void;
}

const BaseButton: React.FC<BaseButtonProps> = ({
  variant,
  disabled,
  children,
  onClick,
  ...rest
}) => (
  <UppButton
    className={"button " + variant}
    variant="no-variant"
    onClick={onClick}
    disabled={!!disabled}
    {...rest}
  >
    {children}
  </UppButton>
);

const boxShadowSize = "0 0 0 0.2rem";

const primaryVariant = css`
  color: ${(props) => props.theme.white};
  background-color: ${(props) => props.theme.primaryColor};
  border-color: ${(props) => props.theme.primaryColor};
  :hover:enabled {
    background-color: ${(props) => props.theme.hoverOver};
  }
  :focus {
    box-shadow: ${boxShadowSize} rgba(225, 83, 97, 0.5);
  }
`;

const primaryOutlineVariant = css`
  color: ${(props) => props.theme.primaryColor};
  background-color: ${(props) => props.theme.white};
  border-color: ${(props) => props.theme.primaryColor};
  :hover:enabled {
    background-color: ${(props) => props.theme.hoverOver};
  }
  :focus {
    box-shadow: ${boxShadowSize} rgba(225, 83, 97, 0.5);
  }
`;

const successVariant = css`
  color: ${(props) => props.theme.white};
  background: ${(props) => props.theme.greenPrimary};
  border-color: ${(props) => props.theme.greenPrimary};
  :hover:enabled {
    background-color: ${(props) => props.theme.green4};
  }
  :focus {
    box-shadow: ${boxShadowSize} rgba(72, 180, 97, 0.5);
  }
`;

const greyVariant = css`
  background-color: ${(props) => props.theme.greyBackground};
  border-color: ${(props) => props.theme.greyAdobe1};
  :hover:enabled {
    background-color: ${(props) => props.theme.greyAdobe1};
  }
  :focus {
    box-shadow: ${boxShadowSize} rgba(146, 146, 152, 0.5);
  }
`;

const defaultVariant = css`
  border: none;
  background-color: transparent;
  :hover:enabled {
    background-color: ${(props) => props.theme.headerBackgroundColor};
  }
  &:focus {
    outline: 0;
    border-color: #80bdff;
    box-shadow: ${boxShadowSize} rgba(0, 123, 255, 0.25);
  }
`;

const getButtonVariant = (variant: string) => {
  switch (variant) {
    case "primary":
      return primaryVariant;
    case "primary-outline":
      return primaryOutlineVariant;
    case "success":
      return successVariant;
    case "grey":
      return greyVariant;
    default:
      return defaultVariant;
  }
};

const baseButtonStyles = css`
  cursor: pointer;
  outline: none;
  border: 1px solid ${(props) => props.theme.greyAdobe1};
  border-radius: 3px;
  padding: ${(props) => props.theme.spacingUnit}px
    ${(props) => props.theme.spacingUnit * 2}px;
  transition: background-color 0.3s;
  display: inline-block;
  ${(props: BaseButtonProps) => {
    return getButtonVariant(props.variant);
  }};
  svg {
    vertical-align: middle;
  }
  &:disabled {
    cursor: not-allowed;
    background: ${(props) => props.theme.greyBackground};
    border-color: ${(props) => props.theme.greyAdobe1};
    color: #aaa;
  }
`;

const Button = styled(BaseButton)`
  ${baseButtonStyles}
`;

export default Button;
